import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import './Documents.css';
import Button from '../../composants/Button/Button';

// Interface pour un document
interface Document {
    id: number;
    key: string;
    type: string;
    category: 'commande' | 'accord_cadre' | 'entreprise';
    filename: string;
    description: string;
    mime_type: string;
    filesize: number;
    created_at: string;
    updated_at: string;
}

// Interface pour la réponse complète de l'API
interface ApiResponse {
    status: string;
    message: string;
    data: {
        contrat_id: number;
        entite_id: number;
        numero_commande: string;
        document_commande: Document[];
        documents_accord_cadre: Document[];
        documents_entreprise: Document[];
        all_documents: Document[];
        total_documents: number;
    };
}

const Documents: React.FC = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const { id } = useParams<{ id: string }>();

    const { user } = useAuth();
    const navigate = useNavigate();
    const [documents, setDocuments] = useState<Document[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>('');
    const [documentTitle, setDocumentTitle] = useState<string>('Documents');
    const [numeroCommande, setNumeroCommande] = useState<string>('');

    // Helper pour formater la taille du fichier
    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    };

    // Helper pour obtenir le libellé de catégorie
    const getCategoryLabel = (category: string): string => {
        switch (category) {
            case 'commande': return 'Bon de commande';
            case 'accord_cadre': return 'Accord-cadre';
            case 'entreprise': return 'Documents entreprise';
            default: return category;
        }
    };

    // Helper pour obtenir un libellé lisible du type
    const getTypeLabel = (type: string): string => {
        // CORE-28: Labels synchronisés avec DocumentEntrepriseMeta::TYPE_LABELS
        const typeLabels: Record<string, string> = {
            'kbis': 'Extrait Kbis',
            'attestation_fiscale': 'Attestation fiscale',
            'rc_pro': 'Assurance RC Pro',
            'declaration_honneur': 'Déclaration sur l\'honneur',
            'attestation_sociale': 'Attestation sociale',
            'certif_rge': 'Certification RGE',
            'rib': 'RIB',
            'garantie_decennale': 'Garantie décennale',
            'agrements': 'Agréments',
            'qualifications': 'Qualifications',
            'logo': 'Logo',
            // Legacy types (pour compatibilité)
            'RIB': 'RIB',
            'Attestation assurance': 'Attestation assurance',
            'Attestation fiscale': 'Attestation fiscale',
            // CORE-28: cv_equipe supprimé (document accord-cadre, pas entité)
        };
        // Si le type commence par "contrat", c'est un contrat signé
        if (type.startsWith('contrat')) return 'Contrat signé';
        // Si le type contient BC-, c'est un bon de commande
        if (type.includes('BC-')) return `Bon de commande ${type}`;
        return typeLabels[type] || type;
    };

    const fetchDocuments = async () => {
        try {
            setLoading(true);

            let apiUrl: string;
            let headers: HeadersInit = {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            };

            if (token) {
                apiUrl = `${process.env.REACT_APP_API_URL}/documents-mobile?token=${token}`;
            } else if (id) {
                apiUrl = `${process.env.REACT_APP_API_URL}/documents-mobile/${id}`;
                const storedToken = localStorage.getItem('token');
                if (storedToken) {
                    headers['Authorization'] = `Bearer ${storedToken}`;
                }
            } else {
                throw new Error('Paramètres manquants dans l\'URL');
            }

            console.log('Récupération des documents depuis:', apiUrl);

            const response = await fetch(apiUrl, { method: 'GET', headers });

            if (!response.ok) {
                throw new Error(`Erreur ${response.status}: ${response.statusText}`);
            }

            const responseData = await response.json() as ApiResponse;

            if (responseData.status === 'success' && responseData.data) {
                // Utiliser all_documents qui contient tous les documents fusionnés
                setDocuments(responseData.data.all_documents || []);
                setNumeroCommande(responseData.data.numero_commande || '');

                if (responseData.data.numero_commande) {
                    setDocumentTitle(`Documents - Commande ${responseData.data.numero_commande}`);
                } else if (responseData.data.contrat_id) {
                    setDocumentTitle(`Documents du contrat ${responseData.data.contrat_id}`);
                }
            } else {
                throw new Error('Format de réponse invalide');
            }
        } catch (err) {
            console.error('Erreur lors de la récupération des documents:', err);
            setError(err instanceof Error ? err.message : 'Erreur lors du chargement des documents');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDocuments();
    }, [token, id]);

    const handleDownload = async (doc: Document) => {
        try {
            // Construire l'URL de téléchargement avec la key du document
            const downloadUrl = `${process.env.REACT_APP_API_URL}/documents-mobile/${doc.key}/download`;

            const response = await fetch(downloadUrl, {
                method: 'GET',
                headers: {
                    'Accept': 'application/pdf,application/octet-stream'
                }
            });

            if (!response.ok) throw new Error('Erreur lors du téléchargement');

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);

            const a = window.document.createElement('a');
            a.href = url;
            a.download = doc.filename || `${doc.type}.pdf`;
            window.document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            window.document.body.removeChild(a);
        } catch (err) {
            console.error('Erreur lors du téléchargement:', err);
            setError('Erreur lors du téléchargement du document');
        }
    };

    if (loading) {
        return (
            <div className="documents-page">
                <div className="loading">
                    <div className="spinner"></div>
                    <p>Chargement des documents...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="documents-page">
                <div className="error-message">
                    <p>{error}</p>
                    <Button variant="secondary" size="small" value="Réessayer" onClick={fetchDocuments} />
                </div>
            </div>
        );
    }

    // Grouper les documents par catégorie
    const documentsByCategory = documents.reduce((acc, doc) => {
        const category = doc.category || 'autre';
        if (!acc[category]) acc[category] = [];
        acc[category].push(doc);
        return acc;
    }, {} as Record<string, Document[]>);

    // Ordre d'affichage des catégories
    const categoryOrder = ['commande', 'accord_cadre', 'entreprise'];

    return (
        <div className="documents-page">
            <div className="documents-header">
                <div className="header-info">
                    <h1>{documentTitle}</h1>
                    <p className="document-count">
                        {documents.length} document{documents.length > 1 ? 's' : ''}
                    </p>
                </div>
            </div>

            <div className="documents-content">
                {documents.length === 0 ? (
                    <div className="no-documents">
                        <p>Aucun document disponible.</p>
                    </div>
                ) : (
                    <div className="documents-sections">
                        {categoryOrder.map(category => {
                            const categoryDocs = documentsByCategory[category];
                            if (!categoryDocs || categoryDocs.length === 0) return null;

                            return (
                                <div key={category} className="documents-category">
                                    <h2 className="category-title">{getCategoryLabel(category)}</h2>
                                    <div className="documents-grid">
                                        {categoryDocs.map((doc) => (
                                            <div
                                                key={doc.id}
                                                className={`document-card ${doc.category === 'commande' ? 'commande-document' : ''}`}
                                                onClick={() => handleDownload(doc)}
                                            >
                                                <div className="document-icon">
                                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M13 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V9z" />
                                                        <polyline points="13 2 13 9 20 9" />
                                                    </svg>
                                                </div>
                                                <div className="document-info">
                                                    <h3 className="document-name">{getTypeLabel(doc.type)}</h3>
                                                    <p className="document-filename">{doc.filename}</p>
                                                    {doc.filesize > 0 && (
                                                        <span className="document-size">{formatFileSize(doc.filesize)}</span>
                                                    )}
                                                </div>
                                                <div className="document-download-icon">
                                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                                                        <polyline points="7 10 12 15 17 10" />
                                                        <line x1="12" y1="15" x2="12" y2="3" />
                                                    </svg>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Documents;